/**
 * Reading an article in the admin — Step 33 rider (Kai: "the backend seeing
 * it, then purposely going into the actual editor to change things").
 *
 * The reference does exactly this: a read page, and a separate `Edit -` page
 * you choose to open. Landing straight in an editor makes every visit an
 * accidental edit risk on content other people rely on, and it hides what
 * the article actually looks like behind a textarea.
 *
 * Actions match the reference's own labels: Copy URL, View on Portal, Edit.
 */
import { useState } from 'react';
import { ExternalLink, Link2, Pencil } from 'lucide-react';
import type { KbArticleDetail, KbCategory } from './api';
import { ArticleBody } from './ArticleBody';
import { ArticleRail } from './ArticleRail';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';

export function ArticleView({
  article,
  categories,
  people,
  onEdit,
}: {
  article: KbArticleDetail;
  categories: KbCategory[];
  people: { id: string; name: string | null; email: string }[];
  onEdit: () => void;
}) {
  const [copied, setCopied] = useState(false);
  // The address a READER uses, not the admin one — that is the whole point of
  // "copy link": it goes in a ticket reply or a chat message.
  const portalUrl = `${window.location.origin}/portal/help/${article.slug}`;

  async function copy() {
    try {
      await navigator.clipboard.writeText(portalUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      // Clipboard is permission-gated and blocked outright in some contexts.
      // The old fallback was `window.prompt`, which is the wrong shape twice
      // over: it is a QUESTION used to display an answer, and it announces
      // the origin ("localhost:3001 says") on a white-label product. A toast
      // carrying the URL is readable and selectable and asks nothing.
      toast.error('Could not copy automatically', { description: portalUrl, duration: 15_000 });
    }
  }

  return (
    <div className="flex min-h-0 flex-1 gap-6 overflow-y-auto p-6">
      <div className="min-w-0 flex-1">
        <div className="mb-4 flex flex-wrap items-start gap-3">
          <div className="min-w-0 flex-1">
            <p className="text-xs text-muted-foreground">{article.folderName}</p>
            <h1 className="mt-0.5 text-xl font-semibold">{article.title}</h1>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => void copy()}>
              <Link2 /> {copied ? 'Copied' : 'Copy URL'}
            </Button>
            <Button
              variant="outline"
              size="sm"
              // window.open rather than an anchor: the Button primitive has no
              // asChild, and a hand-rolled <a> styled as a button is exactly
              // what audit-ui[primitives] exists to stop.
              onClick={() => window.open(portalUrl, '_blank', 'noopener,noreferrer')}
            >
              <ExternalLink /> View on portal
            </Button>
            <Button size="sm" onClick={onEdit}>
              <Pencil /> Edit
            </Button>
          </div>
        </div>

        {article.status !== 'published' && (
          <p className="mb-4 rounded-md border border-amber-500/30 bg-amber-500/10 p-2.5 text-xs text-amber-700 dark:text-amber-400">
            This is a draft — nobody outside the support team can see it yet, whatever its folder allows.
          </p>
        )}

        <ArticleBody html={article.bodyHtml} />
      </div>
      <ArticleRail article={article} categories={categories} people={people} />
    </div>
  );
}
